/**
 * Key Management for Signal Protocol (X3DH + Double Ratchet)
 *
 * Uses @noble/curves for Curve25519 (X25519 key exchange + Ed25519 signing)
 * All crypto runs client-side — server never sees private keys.
 *
 * Key Types:
 * - Identity Key Pair: Long-term Curve25519 key pair (one per device)
 * - Signed Pre-Key: Medium-term key, signed by identity key (rotated weekly)
 * - One-Time Pre-Keys: Ephemeral keys for X3DH (consumed on first use)
 * - Ratchet Keys: Per-message keys derived via Double Ratchet
 */

import { x25519 } from "@noble/curves/ed25519";
import { ed25519 } from "@noble/curves/ed25519";
import { randomBytes } from "@noble/hashes/utils";

// ============================================================
// Types
// ============================================================
export interface KeyPair {
  privateKey: Uint8Array; // 32 bytes
  publicKey: Uint8Array; // 32 bytes
}

export interface SignedPreKey {
  keyId: number;
  keyPair: KeyPair;
  signature: Uint8Array; // Ed25519 signature of the public key
}

export interface PreKey {
  keyId: number;
  keyPair: KeyPair;
}

/**
 * Reserved keyId for the last-resort / fallback pre-key (Signal MSC "last resort
 * key"). The server serves this in the OPK slot when a device's one-time pre-key
 * pool is exhausted, and NEVER deletes it — so a recipient is always encryptable
 * with a dedicated key (better forward secrecy than reusing the signed pre-key).
 * Picked far above any incrementing one-time-prekey id (which start at 1 and grow
 * in small batches) and well under int4 max so it round-trips through Postgres.
 */
export const FALLBACK_PREKEY_ID = 2_000_000_000;

/** How many *previous* signed pre-keys a device retains so it can still decrypt
 *  X3DH wraps built against a recently-rotated SPK. Current + this many = total
 *  decryptable SPKs. Balances forward secrecy against the stale-bundle window:
 *  a sender with a cached bundle that is up to MAX_SPK_HISTORY rotations behind
 *  the current SPK can still complete X3DH successfully. Raised from 2→5 to
 *  cover senders who cache the bundle across longer offline periods or slow
 *  rotation cycles (P1.5). */
export const MAX_SPK_HISTORY = 5;

export interface IdentityKeyPair {
  curve25519: KeyPair; // For X25519 key exchange
  ed25519: {
    privateKey: Uint8Array;
    publicKey: Uint8Array;
  }; // For signing
}

export interface DeviceKeys {
  identityKeyPair: IdentityKeyPair;
  signedPreKey: SignedPreKey;
  /** Recently-rotated signed pre-keys, newest-first, max MAX_SPK_HISTORY.
   *  A receiver selects by usedSignedPreKeyId across { current ∪ these }. */
  previousSignedPreKeys?: SignedPreKey[];
  preKeys: PreKey[];
  /** Last-resort pre-key — served when the one-time pool is empty, never consumed. */
  fallbackPreKey?: PreKey;
}

export interface PublicDeviceKeys {
  identityKey: string; // base64
  signingKey: string; // base64
  signedPreKey: {
    keyId: number;
    publicKey: string; // base64
    signature: string; // base64
  };
  preKeys: Array<{
    keyId: number;
    publicKey: string; // base64
  }>;
  fallbackPreKey?: {
    keyId: number;
    publicKey: string; // base64
  };
}

// ============================================================
// Key Generation
// ============================================================

/** Generate a new X25519 key pair for Diffie-Hellman key exchange */
export function generateX25519KeyPair(): KeyPair {
  const privateKey = randomBytes(32);
  const publicKey = x25519.getPublicKey(privateKey);
  return { privateKey, publicKey };
}

/** Generate a new Ed25519 key pair for signing */
export function generateEd25519KeyPair(): { privateKey: Uint8Array; publicKey: Uint8Array } {
  const privateKey = ed25519.utils.randomPrivateKey();
  const publicKey = ed25519.getPublicKey(privateKey);
  return { privateKey, publicKey };
}

/** Generate a complete identity key pair (both exchange and signing keys) */
export function generateIdentityKeyPair(): IdentityKeyPair {
  return {
    curve25519: generateX25519KeyPair(),
    ed25519: generateEd25519KeyPair(),
  };
}

/** Generate a signed pre-key, signed by the identity's Ed25519 key */
export function generateSignedPreKey(
  identityKey: IdentityKeyPair,
  keyId: number,
): SignedPreKey {
  const keyPair = generateX25519KeyPair();
  const signature = ed25519.sign(keyPair.publicKey, identityKey.ed25519.privateKey);

  return { keyId, keyPair, signature };
}

/** Verify a signed pre-key's signature */
export function verifySignedPreKey(
  publicKey: Uint8Array,
  signature: Uint8Array,
  signingPublicKey: Uint8Array,
): boolean {
  try {
    return ed25519.verify(signature, publicKey, signingPublicKey);
  } catch {
    return false;
  }
}

/** Generate a batch of one-time pre-keys */
export function generatePreKeys(startId: number, count: number): PreKey[] {
  const preKeys: PreKey[] = [];
  for (let i = 0; i < count; i++) {
    preKeys.push({
      keyId: startId + i,
      keyPair: generateX25519KeyPair(),
    });
  }
  return preKeys;
}

/** Generate a fresh last-resort / fallback pre-key (reserved keyId). */
export function generateFallbackPreKey(): PreKey {
  return { keyId: FALLBACK_PREKEY_ID, keyPair: generateX25519KeyPair() };
}

/** Generate all device keys (identity + signed pre-key + one-time pre-keys + fallback).
 *  @param spkKeyId initial signed-pre-key id. Callers in the web client pass a
 *  device-unique id from the persistent allocator (lib/spk-id.ts). Defaults to 1
 *  only for unit tests / legacy callers. */
export function generateDeviceKeys(preKeyCount: number = 20, spkKeyId: number = 1): DeviceKeys {
  const identityKeyPair = generateIdentityKeyPair();
  const signedPreKey = generateSignedPreKey(identityKeyPair, spkKeyId);
  const preKeys = generatePreKeys(1, preKeyCount);
  const fallbackPreKey = generateFallbackPreKey();

  return { identityKeyPair, signedPreKey, preKeys, fallbackPreKey };
}

/** Rotate the signed pre-key: current SPK moves to the front of the history ring
 *  (capped at MAX_SPK_HISTORY), a fresh SPK with `newKeyId` becomes current. Pure
 *  — returns a new DeviceKeys; does not mutate the input. */
export function rotateSignedPreKey(deviceKeys: DeviceKeys, newKeyId: number): DeviceKeys {
  const fresh = generateSignedPreKey(deviceKeys.identityKeyPair, newKeyId);
  const history = [deviceKeys.signedPreKey, ...(deviceKeys.previousSignedPreKeys ?? [])]
    .slice(0, MAX_SPK_HISTORY);
  return { ...deviceKeys, signedPreKey: fresh, previousSignedPreKeys: history };
}

/** Find a signed pre-key by id across the current SPK and the retained history.
 *  Returns null when this device never held that keyId. */
export function findSignedPreKeyById(deviceKeys: DeviceKeys, keyId: number): SignedPreKey | null {
  if (deviceKeys.signedPreKey.keyId === keyId) return deviceKeys.signedPreKey;
  return (deviceKeys.previousSignedPreKeys ?? []).find((s) => s.keyId === keyId) ?? null;
}

// ============================================================
// X3DH Key Exchange
// ============================================================

/** Perform X25519 Diffie-Hellman key exchange */
export function x25519DH(privateKey: Uint8Array, publicKey: Uint8Array): Uint8Array {
  return x25519.getSharedSecret(privateKey, publicKey);
}

// ============================================================
// Serialization (for network transport)
// ============================================================

export function toBase64(data: Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(data).toString("base64");
  }
  return btoa(String.fromCharCode(...data));
}

export function fromBase64(data: string): Uint8Array {
  if (typeof Buffer !== "undefined") {
    return new Uint8Array(Buffer.from(data, "base64"));
  }
  return new Uint8Array(atob(data).split("").map((c) => c.charCodeAt(0)));
}

/** Export device keys to public format (safe to send to server) */
export function exportPublicKeys(deviceKeys: DeviceKeys): PublicDeviceKeys {
  return {
    identityKey: toBase64(deviceKeys.identityKeyPair.curve25519.publicKey),
    signingKey: toBase64(deviceKeys.identityKeyPair.ed25519.publicKey),
    signedPreKey: {
      keyId: deviceKeys.signedPreKey.keyId,
      publicKey: toBase64(deviceKeys.signedPreKey.keyPair.publicKey),
      signature: toBase64(deviceKeys.signedPreKey.signature),
    },
    preKeys: deviceKeys.preKeys.map((pk) => ({
      keyId: pk.keyId,
      publicKey: toBase64(pk.keyPair.publicKey),
    })),
    ...(deviceKeys.fallbackPreKey
      ? {
          fallbackPreKey: {
            keyId: deviceKeys.fallbackPreKey.keyId,
            publicKey: toBase64(deviceKeys.fallbackPreKey.keyPair.publicKey),
          },
        }
      : {}),
  };
}
